<template>
  <div class="relative">
    <Combobox
      ignore-filter
      by="id" :model-value="selectedProduct" :disabled="disabled"
      @update:model-value="(value) => onProductSelect(value as T | null)"
    >
      <ComboboxAnchor class="w-auto cursor-text">
        <div class="relative w-full items-center">
          <ComboboxInput
            :class="cn('pr-9 bg-white cursor-text', inputClass)" :display-value="(product) => (product as T | null)?.name ?? ''" :placeholder="placeholder"
            @input="onSearchInput"
          />
          <span class="absolute end-0 inset-y-0 flex items-center justify-center px-3">
            <Search class="size-4 text-muted-foreground" />
          </span>
        </div>
      </ComboboxAnchor>

      <ComboboxList class="w-[var(--reka-combobox-trigger-width)] max-h-72 overflow-y-auto">
        <ComboboxEmpty>
          <div v-if="isLoading" class="flex items-center justify-center py-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
            Recherche en cours...
          </div>
          <div v-else>
            Aucun produit trouvé
          </div>
        </ComboboxEmpty>

        <ComboboxGroup>
          <ComboboxItem v-for="product in (products ?? [])" :key="product.id" :value="product">
            {{ product.name }}
          </ComboboxItem>
        </ComboboxGroup>
      </ComboboxList>
    </Combobox>
  </div>
</template>

<script setup lang="ts" generic="T extends {id: number; name: string} = LegacyProduct">
import {
    Combobox,
    ComboboxAnchor,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxList
} from "@/core/components/ui/combobox";
import {LegacyProduct} from "@/core/interfaces/Product";
import {computed, onUnmounted, ref} from "vue";
import {productApi} from "@/container";
import debounce from "lodash.debounce";
import {Search} from "@lucide/vue";
import {cn} from "@/lib/utils";

interface Props {
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    inputClass?: string;
    /**
     * Custom search function. Defaults to the global product search.
     * Provide a scoped function (e.g. a loyalty-card range) to restrict results.
     */
    searchFn?: (query: string) => Promise<T[]>;
}

const {disabled = false, loading = false, placeholder = 'Rechercher un produit', searchFn} = defineProps<Props>();
const emit = defineEmits<{
    (e: 'select', product: T)
}>();

const search = ref<string>('');
const selectedProduct = ref<T | null>(null);
const products = ref<T[]>([]);
const localLoading = ref<boolean>(false);

const isLoading = computed(() => loading || localLoading.value);

/**
 * Search for a product, using the scoped search function when provided.
 */
const searchProduct = (searchQuery: string): Promise<T[]> => {
    if (searchFn) {
        return searchFn(searchQuery);
    }

    return productApi().search(searchQuery) as unknown as Promise<T[]>;
};

/**
 * Perform the search if not already loading and if the value is different from the selected product.
 */
const performSearch = async (value: string) => {
    if (localLoading.value) {
        return;
    }

    if (!value || value === selectedProduct.value?.name) {
        return;
    }

    localLoading.value = true;

    try {
        products.value = await searchProduct(value);
    } finally {
        localLoading.value = false;
    }
};

const debouncedSearch = debounce(performSearch, 300);

/**
 * Handle the input event on the search bar.
 */
const onSearchInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    search.value = target.value;
    debouncedSearch(target.value);
};

/**
 * Handle the selection of a product.
 */
const onProductSelect = (product: T | null) => {
    selectedProduct.value = product;

    if (product) {
        emit('select', product);
    }
};

onUnmounted(() => {
    debouncedSearch.cancel();
});
</script>

<style>

</style>
